import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import Theme from 'core/themes/model'
import { bottomLeft } from './defaults'
import {
  getMenuTransform,
  getPortalMenuBottom,
  getPortalMenuLeft,
  getPortalMenuTop,
} from './helpers'
import { MenuPlacementProps } from './model'

export interface IMenuProps extends MenuPlacementProps {
  open: boolean
  anchor: JSX.Element
  origin?: string
  unorderedList?: boolean
  className?: string
  onClose?: () => void
}

type Props = IMenuProps &
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

// @TODO Implement align and offset for PortalMenu
export default function PortalMenu({
  open = false,
  anchor,
  align = bottomLeft.align,
  offset = bottomLeft.offset,
  origin = 'center center',
  unorderedList = false,
  onClose,
  className,
  children,
  ...props
}: PropsWithChildren<Props>) {
  const classes = useStyles()
  const menuContainerRef = useRef<HTMLDivElement>(undefined)
  const menuRef = useRef<HTMLElement>(null)
  // Guards against firing onClose more than once for a single open session, so a
  // burst of scroll events can't toggle a consumer's state back open. [PCD-9652]
  const closedRef = useRef(false)
  const rect = menuContainerRef?.current?.getBoundingClientRect()

  // Cap the menu's height to the viewport space available in its opening direction
  // and let it scroll internally, so a long options list stays reachable instead
  // of overflowing off-screen.
  const menuMaxHeight = useMemo(() => {
    if (!rect) return undefined
    const margin = 16
    if (align.vertical === 'top') {
      return Math.max(rect.top - margin, 0)
    }
    return Math.max(window.innerHeight - rect.top - margin, 0)
  }, [rect, align])

  const menuStyles = useMemo(
    () => ({
      transformOrigin: origin,
      top: getPortalMenuTop({ rect, vertAlign: align.vertical, vertOffset: offset.vertical }),
      bottom: getPortalMenuBottom({ rect, vertAlign: align.vertical, vertOffset: offset.vertical }),
      left: getPortalMenuLeft({
        rect,
        horizAlign: align.horizontal,
        horizOffset: offset.horizontal,
      }),
      maxHeight: menuMaxHeight,
      overflowY: 'auto' as const,
    }),
    [origin, rect, align, offset, menuMaxHeight],
  )

  // Dismiss the menu on any standard gesture: pointer press outside the anchor
  // or the menu, the Escape key, or scrolling outside the menu. Scrolling
  // closes (rather than repositions) because the menu is portaled at a fixed
  // offset and would otherwise stay pinned to viewport coordinates and detach
  // from its anchor. A scroll that happens inside the menu itself (e.g. the
  // options list scrollbar added for long lists) does not close it, so long
  // lists stay browsable. Closing on an outside press also means opening
  // another menu closes this one, so at most one menu is ever open at a time.
  // [PCD-9652] [PCD-9775]
  useEffect(() => {
    if (!open) {
      closedRef.current = false
      return undefined
    }
    const close = () => {
      if (closedRef.current || !onClose) return
      closedRef.current = true
      onClose()
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuContainerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return
      }
      close()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    const handleScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && menuRef.current?.contains(target)) {
        return
      }
      close()
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    // Capture phase so scrolls inside any nested scroll container dismiss too.
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open, onClose])

  const menuElement = unorderedList ? 'ul' : 'nav'
  const MenuComponent = React.createElement(
    menuElement,
    {
      className: clsx(classes.menu, 'menu-popover', { active: open }),
      ref: menuRef,
      style: menuStyles,
    },
    children,
  )
  return (
    <div ref={menuContainerRef} className={clsx(classes.menuContainer, className)} {...props}>
      {anchor}
      {open && <PortalMenuComponent MenuComponent={MenuComponent} />}
    </div>
  )
}

const PortalMenuComponent = ({ MenuComponent }) => {
  const portalElem = useMemo(() => document.getElementById('row-menu-portal-root'), [])
  if (!MenuComponent) return null
  return ReactDOM.createPortal(MenuComponent, portalElem)
}

const useStyles = makeStyles<Theme>((theme) => ({
  menuContainer: {
    position: 'relative',
  },
  menu: {
    minWidth: 220,
    position: 'absolute',
    opacity: 0,
    backgroundColor: theme.components.card.background,
    border: `1px solid ${theme.components.card.border}`,
    borderRadius: 4,
    transition: 'opacity .2s ease, transform .2s ease',
    zIndex: 1000,
    transform: getMenuTransform(0),
    '&.active': {
      opacity: 1,
      transform: `scale(1) translate(0%, -20%)`,
    },
  },
}))
